/**
 * Truecaller Web SDK Integration for Mobile Web Login
 *
 * Flow:
 * 1. Generate a unique requestNonce
 * 2. Trigger the `truecallersdk://` deep link, this opens Truecaller app on Android
 * 3. Detect if Truecaller app is installed using document.hasFocus() check after a delay
 * 4. If installed: Truecaller sends the user's access token to our backend callback URL
 * 5. Frontend polls our status endpoint every second until login completes
 * 6. If not installed: fall back to OTP flow
 *
 * Docs: https://docs.truecaller.com/truecaller-sdk/web/getting-started
 */

export interface TruecallerProfile {
  phoneNumber: string;
  firstName: string;
  lastName?: string;
  email?: string;
}

export interface TruecallerLoginResult {
  success: boolean;
  customer?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    has_account: boolean;
    metadata?: Record<string, any>;
  };
  token?: string;
  error?: string;
}

const PARTNER_KEY = process.env.NEXT_PUBLIC_TRUECALLER_APP_KEY || '';
const PARTNER_NAME = 'Hinch';

/**
 * Generate a URL-safe random nonce (16-32 chars)
 */
export function generateRequestNonce(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const array = new Uint8Array(24);
  crypto.getRandomValues(array);
  array.forEach((byte) => {
    result += chars[byte % chars.length];
  });
  return result;
}

/**
 * Check if running on a mobile Android device
 */
export function isAndroidMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return /android/i.test(navigator.userAgent);
}

/**
 * Trigger the Truecaller SDK deep link.
 * This will open the Truecaller app on Android devices that have it installed.
 *
 * @param nonce - Unique request nonce for this session
 * @returns The deep link URL that was triggered
 */
export function triggerTruecallerDeepLink(nonce: string): string {
  // Build params manually (NOT using URLSearchParams) to avoid double-encoding
  // Truecaller expects raw values for color hex params
  const partnerKey = encodeURIComponent(PARTNER_KEY);
  const partnerName = encodeURIComponent(PARTNER_NAME);
  const nonceEncoded = encodeURIComponent(nonce);
  const skipOption = encodeURIComponent('Use another number');

  const deepLink = [
    `truecallersdk://truesdk/web_verify`,
    `?requestNonce=${nonceEncoded}`,
    `&partnerKey=${partnerKey}`,
    `&partnerName=${partnerName}`,
    `&lang=en`,
    `&skipOption=${skipOption}`,
    `&loginPrefix=continue_with`,
    `&loginSuffix=to_continue`,
    `&ctaColor=%23EA580C`,
    `&ctaTextColor=%23FFFFFF`,
    `&btnShape=round`,
    `&title=hi_there`,
    `&ttl=600000`,
  ].join('');

  console.log('🔗 Triggering Truecaller deep link:', deepLink);

  // Navigate to deep link - this opens the Truecaller app
  window.location.href = deepLink;

  return deepLink;
}

/**
 * Detect whether Truecaller app is installed.
 *
 * After triggering the deep link, if Truecaller is installed the browser loses
 * focus as the app opens. If Truecaller is NOT installed, the browser window
 * retains focus.
 *
 * @returns Promise<boolean> - true if Truecaller app is available
 */
export function detectTruecallerApp(): Promise<boolean> {
  return new Promise((resolve) => {
    // Give the deep link time to switch to the Truecaller app.
    // 800ms is recommended: slower devices need more time.
    setTimeout(() => {
      const appNotInstalled = document.hasFocus();
      console.log(
        appNotInstalled
          ? '❌ Truecaller not installed (page still has focus)'
          : '✅ Truecaller app opened (page lost focus)'
      );
      resolve(!appNotInstalled);
    }, 800);
  });
}

/**
 * Poll our backend status endpoint until the login is complete or times out.
 * Called after the Truecaller deep link is triggered and app is detected.
 *
 * @param nonce - The same nonce used to trigger the deep link
 * @param maxAttempts - Maximum number of 1-second polls (default: 30 = 30 seconds)
 * @returns Promise<TruecallerLoginResult>
 */
export async function pollTruecallerLoginStatus(
  nonce: string,
  maxAttempts = 30
): Promise<TruecallerLoginResult> {
  console.log(`🔄 Starting to poll for Truecaller login status (nonce: ${nonce})`);

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise((r) => setTimeout(r, 1000)); // wait 1 second

    try {
      const response = await fetch(
        `/api/auth/truecaller/status?nonce=${encodeURIComponent(nonce)}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        console.warn(`⚠️ Status poll attempt ${attempt + 1} returned ${response.status}`);
        continue;
      }

      const data = await response.json();
      console.log(`📊 Poll attempt ${attempt + 1}:`, data.status);

      if (data.status === 'success') {
        console.log('✅ Truecaller login completed successfully');
        return { success: true, customer: data.customer, token: data.token };
      }

      if (data.status === 'error') {
        console.error('❌ Truecaller login failed:', data.error);
        return { success: false, error: data.error || 'Truecaller verification failed' };
      }

      // status === 'pending' → keep polling
    } catch (error) {
      console.warn(`⚠️ Poll attempt ${attempt + 1} error:`, error);
    }
  }

  console.warn('⏰ Truecaller login polling timed out');
  return { success: false, error: 'Verification timed out. Please try again.' };
}

/**
 * Full Truecaller login flow:
 * 1. Generate nonce
 * 2. Trigger deep link
 * 3. Detect if app is installed
 * 4. If yes, poll for completion
 *
 * @returns {nonce, appDetected, resultPromise}
 */
export async function initiateTruecallerLogin(): Promise<{
  nonce: string;
  appDetected: boolean;
  resultPromise?: Promise<TruecallerLoginResult>;
}> {
  const nonce = generateRequestNonce();

  // Store nonce in session storage for retrieval on page focus
  sessionStorage.setItem('tc_nonce', nonce);
  sessionStorage.setItem('tc_initiated_at', Date.now().toString());

  // Trigger the deep link
  triggerTruecallerDeepLink(nonce);

  // Detect if app is installed
  const appDetected = await detectTruecallerApp();

  if (!appDetected) {
    sessionStorage.removeItem('tc_nonce');
    sessionStorage.removeItem('tc_initiated_at');
    return { nonce, appDetected: false };
  }

  // Start polling for login result
  const resultPromise = pollTruecallerLoginStatus(nonce);
  return { nonce, appDetected: true, resultPromise };
}
